/**
 * Утилита для обработки логики разделения экрана и выбора отчетов
 * Основана на анализе рабочей реализации из BasicReport.js
 */

class SplitScreenReportLogic {
  constructor(containerId, chartContainerRef, setShowReportChooser, setContainerToFill, setSplitDirection, showReportChooser) {
    this.containerId = containerId;
    this.chartContainerRef = chartContainerRef;
    this.setShowReportChooser = setShowReportChooser;
    this.setContainerToFill = setContainerToFill;
    this.setSplitDirection = setSplitDirection;
    this.showReportChooser = showReportChooser;
    
    // Локальные переменные для флагов (как в BasicReport.js)
    this.modalTriggered = false;
    this.modalRequested = false;
    
    // Привязываем методы к контексту
    this.handleSplitComplete = this.handleSplitComplete.bind(this);
    this.handleRequestReportSelector = this.handleRequestReportSelector.bind(this);
    this.resetModalFlag = this.resetModalFlag.bind(this);
    this.resetRequestFlag = this.resetRequestFlag.bind(this);
  }

  /**
   * Обработчик кнопки горизонтального разделения
   */
  handleHorizontalSplit() {
    const container = this.chartContainerRef.current?.closest('.split-screen-container');
    if (!container) {
      console.error('BaseChart: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    this.activateContainer(container);
    
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('BaseChart: У контейнера нет ID для разделения');
      return;
    }
      
    console.log(`BaseChart: Разделяем контейнер ${targetContainerId} горизонтально`);
    
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'horizontal',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
    
    // После отправки события закрываем выбор отчетов, если он открыт
    if (this.showReportChooser) {
      this.setShowReportChooser(false);
    }
  }

  /**
   * Обработчик кнопки вертикального разделения
   */
  handleVerticalSplit() {
    const container = this.chartContainerRef.current?.closest('.split-screen-container');
    if (!container) {
      console.error('BaseChart: Не удалось найти контейнер для разделения');
      return;
    }
    
    // Активируем контейнер
    this.activateContainer(container);
    
    const targetContainerId = container.id || container.getAttribute('data-container-id');
    if (!targetContainerId) {
      console.error('BaseChart: У контейнера нет ID для разделения');
      return;
    }
    
    console.log(`BaseChart: Разделяем контейнер ${targetContainerId} вертикально`);
    
    const splitEvent = new CustomEvent('requestSplit', {
      detail: {
        direction: 'vertical',
        targetContainerId: targetContainerId,
        timestamp: Date.now(),
        processed: false
      }
    });
    
    document.dispatchEvent(splitEvent);
    
    // После отправки события закрываем выбор отчетов, если он открыт
    if (this.showReportChooser) {
      this.setShowReportChooser(false);
    }
  }

  /**
   * Активация контейнера
   */
  activateContainer(container) {
    document.querySelectorAll('.split-screen-container[data-active="true"]')
      .forEach(el => {
        el.setAttribute('data-active', 'false');
        el.classList.remove('active-container');
      });
    
    container.setAttribute('data-active', 'true');
    container.classList.add('active-container');
  }

  /**
   * Обработчик события завершения разделения (точно как в BasicReport.js)
   */
  handleSplitComplete(event) {
    const { containerId: eventContainerId, container1Id, container2Id, direction } = event.detail;
    
    console.log(`BaseChart: Получено событие завершения разделения`, {
      eventContainerId,
      myContainerId: this.containerId,
      container1Id,
      container2Id,
      direction,
      modalTriggered: this.modalTriggered
    });
    
    // Проверяем, что это событие связано с нашим контейнером
    // Событие может прийти для родительского контейнера или для нашего контейнера
    const isRelatedToMyContainer = eventContainerId === this.containerId || 
                                  this.containerId.startsWith(eventContainerId) ||
                                  eventContainerId.startsWith(this.containerId.split('-')[0]);
    
    if (isRelatedToMyContainer) {
      console.log(`BaseChart: Обрабатываем событие разделения для контейнера ${this.containerId}`);
      
      // Сбрасываем флаги для нового разделения
      this.modalTriggered = false;
      this.modalRequested = false;
      
      // Проверяем глобальный флаг
      if (window.reportChooserModalOpen && window.reportChooserModalOpen !== true) {
        console.log(`BaseChart: Модальное окно уже открыто в другом контейнере: ${window.reportChooserModalOpen}`);
        return;
      }
      
      console.log(`BaseChart: Новые контейнеры: ${container1Id}, ${container2Id}, направление: ${direction}`);
      
      // Устанавливаем флаг, что модальное окно вызвано для этого разделения
      this.modalTriggered = true;
      
      // Устанавливаем глобальный флаг
      window.reportChooserModalOpen = container2Id;
      
      // Открываем окно выбора отчета для второго контейнера
      this.setContainerToFill(container2Id);
      this.setShowReportChooser(true);
      
      console.log(`BaseChart: Открываем ReportChooser для контейнера ${container2Id}`);
      
      // Активируем второй контейнер для корректной работы кнопок
      setTimeout(() => {
        const container2 = document.getElementById(container2Id) || 
                         document.querySelector(`[data-container-id="${container2Id}"]`);
        if (container2) {
          // Сначала снимаем активность со всех контейнеров
          document.querySelectorAll('.split-screen-container[data-active="true"]')
            .forEach(el => {
              if (el && document.body.contains(el)) { // Проверяем, что элемент всё ещё в DOM
                el.setAttribute('data-active', 'false');
                el.classList.remove('active-container');
              }
            });
          
          // Активируем новый контейнер
          container2.setAttribute('data-active', 'true');
          container2.classList.add('active-container');
          
          console.log(`BaseChart: Активирован новый контейнер ${container2Id} после разделения`);
        }
        
        // Принудительно запускаем регистрацию всех графиков после разделения
        this.forceScanAndRegisterCharts();
      }, 300);
      
      // Сбрасываем флаг при закрытии модального окна через некоторое время
      setTimeout(() => {
        this.resetModalFlag();
        // Сбрасываем глобальный флаг, если он установлен на наш контейнер
        if (window.reportChooserModalOpen === container2Id) {
          window.reportChooserModalOpen = false;
        }
      }, 3000); // Увеличиваем время до 3 секунд
    } else {
      console.log(`BaseChart: Пропускаем событие разделения - не связано с нашим контейнером ${this.containerId}`);
    }
  }

  /**
   * Принудительное сканирование и регистрация графиков
   */
  forceScanAndRegisterCharts() {
    console.log('BaseChart: Принудительное сканирование и регистрация графиков после разделения');
    
    // Ищем все canvas элементы графиков
    const canvasElements = document.querySelectorAll('canvas[data-graph="true"], .chart-content canvas, [role="img"] canvas');
    
    let registeredCount = 0;
    let foundCharts = [];
    
    canvasElements.forEach(canvas => {
      // Пытаемся найти экземпляр графика
      let chartInstance = null;
      
      // Способ 1: через __chartjs__ (стандартный способ Chart.js)
      if (canvas.__chartjs__) {
        chartInstance = canvas.__chartjs__;
      }
      // Способ 2: через __chartInstance (наш способ)
      else if (canvas.__chartInstance) {
        chartInstance = canvas.__chartInstance;
      }
      // Способ 3: через Chart.js registry
      else if (window.Chart && window.Chart.instances) {
        for (let instance of Object.values(window.Chart.instances)) {
          if (instance.canvas === canvas) {
            chartInstance = instance;
            break;
          }
        }
      }
      
      if (chartInstance && !chartInstance.isDestroyed) {
        // Получаем ID контейнера
        let containerId = canvas.getAttribute('data-container-id');
        
        if (!containerId) {
          // Пытаемся найти контейнер по родительским элементам
          let parent = canvas.parentElement;
          while (parent && !containerId) {
            if (parent.id && parent.id.startsWith('chart-container-')) {
              containerId = parent.id;
              break;
            }
            containerId = parent.getAttribute('data-container-id');
            if (containerId) break;
            parent = parent.parentElement;
          }
        }
        
        if (containerId) {
          foundCharts.push({
            containerId,
            chartInstance,
            canvas
          });
          
          // Проверяем, зарегистрирован ли график в ChartSyncManager
          if (window.chartSyncManager && !window.chartSyncManager.charts.has(containerId)) {
            console.log(`BaseChart: Регистрируем найденный график ${containerId} в ChartSyncManager`);
            
            // Устанавливаем необходимые атрибуты
            canvas.setAttribute('data-graph', 'true');
            canvas.setAttribute('data-container-id', containerId);
            canvas.__chartjs__ = chartInstance;
            canvas.__chartInstance = chartInstance;
            
            // Регистрируем в менеджере
            try {
              window.chartSyncManager.registerChart(containerId, chartInstance);
              registeredCount++;
              console.log(`BaseChart: График ${containerId} успешно зарегистрирован`);
            } catch (error) {
              console.error(`BaseChart: Ошибка при регистрации графика ${containerId}:`, error);
            }
          }
        }
      }
    });
    
    console.log(`BaseChart: Принудительное сканирование завершено. Найдено графиков: ${foundCharts.length}, зарегистрировано: ${registeredCount}`);
    
    // Отправляем событие о готовности синхронизации
    if (foundCharts.length > 0) {
      document.dispatchEvent(new CustomEvent('chartSyncReady', {
        detail: {
          totalCharts: foundCharts.length,
          newCharts: registeredCount,
          charts: foundCharts.map(c => c.containerId),
          timestamp: Date.now()
        }
      }));
    }
    
    // Дополнительно запускаем сканирование в ChartSyncManager
    if (window.chartSyncManager && window.chartSyncManager.scanForNewCharts) {
      setTimeout(() => {
        console.log('BaseChart: Запускаем дополнительное сканирование в ChartSyncManager');
        window.chartSyncManager.scanForNewCharts(true);
      }, 500);
    }
  }

  /**
   * Обработчик запроса выбора отчета (точно как в BasicReport.js)
   */
  handleRequestReportSelector(event) {
    const { containerId, direction, activateContainer } = event.detail;
    
    // Проверяем глобальный флаг, что модальное окно еще не открыто где-то
    if (window.reportChooserModalOpen) {
      console.log(`BaseChart: Пропускаем запрос выбора отчета, модальное окно уже открыто в другом экземпляре`);
      return;
    }
    
    // Проверяем локальное состояние
    if (this.showReportChooser || this.modalRequested) {
      console.log(`BaseChart: Пропускаем повторный запрос выбора отчета, так как модальное окно уже ${this.showReportChooser ? 'открыто' : 'запрошено'}`);
      return;
    }
    
    // Устанавливаем флаг, что запрос обработан
    this.modalRequested = true;
    // Устанавливаем глобальный флаг
    window.reportChooserModalOpen = true;
    
    console.log(`BaseChart: Получен запрос на открытие селектора отчетов для контейнера ${containerId}`);
    
    // Устанавливаем целевой контейнер и открываем выбор отчета
    this.setContainerToFill(containerId);
    
    // Активируем контейнер для выбора отчета, если требуется
    if (activateContainer) {
      const container = document.getElementById(containerId) || 
                       document.querySelector(`[data-container-id="${containerId}"]`);
      
      if (container) {
        // Сначала снимаем активность со всех контейнеров
        document.querySelectorAll('.split-screen-container[data-active="true"]')
          .forEach(el => {
            if (el && document.body.contains(el)) { // Проверяем, что элемент всё ещё в DOM
              el.setAttribute('data-active', 'false');
              el.classList.remove('active-container');
            }
          });
        
        // Активируем наш контейнер, если он всё ещё в DOM
        if (document.body.contains(container)) {
          container.setAttribute('data-active', 'true');
          container.classList.add('active-container');
          
          console.log(`BaseChart: Активирован контейнер ${containerId} для выбора отчета`);
          
          // Добавляем отложенное обновление активного состояния через 100мс
          // чтобы гарантировать, что оно применится после всех DOM-операций
          setTimeout(() => {
            if (document.body.contains(container)) {
              container.setAttribute('data-active', 'true');
              container.classList.add('active-container');
              console.log(`BaseChart: Повторно активирован контейнер ${containerId} (отложенное обновление)`);
            }
          }, 100);
        }
      }
    }
    
    // Открываем выбор отчета
    console.log('BaseChart: Открываем диалог выбора отчета (setShowReportChooser(true))');
    this.setShowReportChooser(true);
    
    // Если указано направление разделения, сохраняем его
    if (direction) {
      this.setSplitDirection(direction);
    }
    
    // Сбрасываем флаг блокировки через некоторое время
    setTimeout(this.resetRequestFlag, 1000);
  }

  /**
   * Сброс флага модального окна
   */
  resetModalFlag() {
    this.modalTriggered = false;
  }

  /**
   * Сброс флага запроса
   */
  resetRequestFlag() {
    this.modalRequested = false;
  }

  /**
   * Обновление состояния showReportChooser (нужно вызывать при изменении)
   */
  updateShowReportChooser(newValue) {
    this.showReportChooser = newValue;
  }

  /**
   * Обработчик выбора отчета (упрощенная версия как в BasicReport.js)
   */
  handleReportSelect(selectedReportType, containerToFill, vehicle, startDate, endDate, splitDirection, containerId) {
    try {
      // Закрываем выбор отчета сразу, чтобы избежать повторных вызовов
      console.log('BaseChart: handleReportSelect - закрываем окно выбора отчета');
      this.setShowReportChooser(false);
      
      // Сбрасываем все флаги
      this.modalTriggered = false;
      this.modalRequested = false;
      console.log('BaseChart: Сбрасываем глобальный флаг после выбора отчета');
      window.reportChooserModalOpen = false;
      
      if (containerToFill) {
        // Если у нас есть контейнер для заполнения из события разделения,
        // создаем отчет для этого контейнера
        console.log(`BaseChart: Создаем отчет типа ${selectedReportType} для контейнера ${containerToFill}`);
        
        // Проверяем существование контейнера в DOM перед созданием отчета
        const targetContainer = document.getElementById(containerToFill) || 
                        document.querySelector(`[data-container-id="${containerToFill}"]`);
                        
        if (!targetContainer) {
          console.warn(`BaseChart: Целевой контейнер ${containerToFill} не найден в DOM, отмена создания отчета`);
          if (window.showNotification) {
            window.showNotification('warning', 'Контейнер для отчета не найден');
          }
          this.setContainerToFill(null);
          return;
        }
      
        // Создаем отчет через систему событий
        const createEvent = new CustomEvent('createReport', {
          detail: {
            reportType: selectedReportType,
            container: containerToFill,
            vehicle: vehicle,
            startDate: startDate,
            endDate: endDate,
            timestamp: Date.now(),
            // Добавляем пометку, что это важное событие
            priority: 'high'
          }
        });
        
        // Отправляем событие без задержки
        document.dispatchEvent(createEvent);
        
        // Добавляем визуальную обратную связь для подтверждения выбора отчета
        if (window.showNotification) {
          window.showNotification('success', `Отчет "${selectedReportType}" добавлен в контейнер`);
        }
        
        // Сбрасываем контейнер
        this.setContainerToFill(null);
        return;
      }
      
      // Проверяем, что направление разделения было установлено
      if (!splitDirection) {
        console.warn('BaseChart: Попытка разделения экрана без указания направления');
        return;
      }
      
      // Создаем событие для создания отчета в контейнере
      const createEvent = new CustomEvent('createReportInContainer', {
        detail: {
          reportType: selectedReportType,
          direction: splitDirection,
          containerId: containerId,
          vehicle: vehicle,
          startDate: startDate,
          endDate: endDate,
          timestamp: Date.now()
        }
      });
      
      document.dispatchEvent(createEvent);
      
      // Сбрасываем направление разделения
      this.setSplitDirection(null);
    } catch (error) {
      console.error('BaseChart: Ошибка при обработке выбора отчета:', error);
      
      // Сбрасываем глобальный флаг в случае ошибки
      window.reportChooserModalOpen = false;
      
      // Показываем ошибку в уведомлении, если доступно
      if (window.showNotification) {
        window.showNotification('error', 'Ошибка при создании отчета: ' + (error.message || 'Неизвестная ошибка'));
      }
    }
  }

  /**
   * Закрытие окна выбора отчета
   */
  handleReportChooserClose() {
    console.log('BaseChart: Закрытие окна выбора отчета');
    this.setShowReportChooser(false);
    this.setSplitDirection(null);
    this.setContainerToFill(null);
    
    // Сбрасываем локальные флаги
    this.modalTriggered = false;
    this.modalRequested = false;
    
    // Сбрасываем глобальный флаг
    console.log('BaseChart: Сбрасываем глобальный флаг reportChooserModalOpen');
    window.reportChooserModalOpen = false;
  }

  /**
   * Подписка на события
   */
  attachEventListeners() {
    document.addEventListener('splitContainerComplete', this.handleSplitComplete);
    document.addEventListener('requestReportSelector', this.handleRequestReportSelector);
  }

  /**
   * Отписка от событий
   */
  detachEventListeners() {
    document.removeEventListener('splitContainerComplete', this.handleSplitComplete);
    document.removeEventListener('requestReportSelector', this.handleRequestReportSelector);
  }

  /**
   * Уничтожение экземпляра
   */
  destroy() {
    this.detachEventListeners();
    this.modalTriggered = false;
    this.modalRequested = false;
  }
}

export default SplitScreenReportLogic; 